<template>
  <main-template>
    <v-row>
      <v-col>
        <h1>
          <v-btn color="primary" x-small text @click="voltar">
            <v-icon left>fa fa-angle-left</v-icon>
          </v-btn>
          {{ editing ? "Editar" : "Novo" }} atendimento
        </h1>
      </v-col>
    </v-row>

    <v-card class="mt-2">
      <v-tabs v-model="tab" background-color="primary" dark>
        <v-tab id="atendimento.tabDados">Dados Principais</v-tab>
        <v-tab id="atendimento.tabFrequencia">Frequência</v-tab>
      </v-tabs>

      <v-tabs-items v-model="tab">
        <!-- ABA 1: DADOS PRINCIPAIS -->
        <v-tab-item>
          <v-card-text>
            <ValidationObserver ref="observer">
              <v-row>
                <v-col cols="12" md="6">
                  <ValidationProvider
                    name="Tipo de Atendimento"
                    rules="required"
                    v-slot="{ errors }"
                  >
                    <v-autocomplete
                      id="atendimento.tipo"
                      v-model="form.tipo_atendimento_id"
                      :items="tipos"
                      item-text="descricao"
                      item-value="id"
                      label="Tipo de Atendimento"
                      outlined
                      dense
                      :error-messages="errors"
                      @change="onChangeTipo"
                    >
                      <template #item="{ item }">
                        <span :id="`atendimento.tipo.${item.id}`">
                          {{ item.descricao }} ({{ item.codigo }})
                        </span>
                      </template>
                    </v-autocomplete>
                  </ValidationProvider>
                </v-col>

                <v-col cols="12" md="6">
                  <ValidationProvider name="Franquia" rules="required" v-slot="{ errors }">
                    <v-autocomplete
                      id="atendimento.franquia"
                      v-model="form.configuracao_id"
                      :items="franquias"
                      item-text="descricao"
                      item-value="id"
                      label="Franquia"
                      class="titulo-maior"
                      outlined
                      dense
                      :error-messages="errors"
                    >
                      <template #item="{ item }">
                        <span :id="`atendimento.franquia.${item.id}`">{{ item.descricao }}</span>
                      </template>
                    </v-autocomplete>
                  </ValidationProvider>
                </v-col>

                <v-col cols="12" md="4">
                  <ValidationProvider name="Data" rules="required" v-slot="{ errors }">
                    <v-menu
                      ref="menuDataRef"
                      v-model="menuData"
                      :close-on-content-click="false"
                      :return-value.sync="form.data"
                      transition="scale-transition"
                      offset-y
                      min-width="auto"
                    >
                      <template #activator="{ on, attrs }">
                        <v-text-field
                          id="atendimento.data"
                          :value="form.data ? formatarData(form.data) : ''"
                          label="Data"
                          class="titulo-maior"
                          outlined
                          dense
                          readonly
                          prepend-inner-icon="mdi-calendar"
                          v-bind="attrs"
                          v-on="on"
                          :error-messages="errors"
                        />
                      </template>
                      <v-date-picker v-model="form.data" locale="pt-BR" scrollable>
                        <v-spacer />
                        <v-btn text color="primary" @click="menuData = false">Cancelar</v-btn>
                        <v-btn text color="primary" @click="$refs.menuDataRef.save(form.data)">
                          OK
                        </v-btn>
                      </v-date-picker>
                    </v-menu>
                  </ValidationProvider>
                </v-col>

                <v-col cols="12" md="4">
                  <ValidationProvider name="Horário" rules="required" v-slot="{ errors }">
                    <v-text-field
                      id="atendimento.horario"
                      v-model="form.horario"
                      type="time"
                      label="Horário"
                      class="horario-pointer titulo-maior"
                      outlined
                      dense
                      :error-messages="errors"
                    />
                  </ValidationProvider>
                </v-col>

                <v-col cols="12" md="4">
                  <ValidationProvider name="Zona" rules="required" v-slot="{ errors }">
                    <v-radio-group
                      id="atendimento.zona"
                      v-model="form.zona"
                      row
                      class="mt-0 ml-md-3"
                      :error-messages="errors"
                    >
                      <template #label>
                        <span class="text-subtitle-1 font-weight-medium mr-3 text--primary">
                          Zona:
                        </span>
                      </template>
                      <v-radio label="Urbana" value="urbana" />
                      <v-radio label="Rural" value="rural" />
                    </v-radio-group>
                  </ValidationProvider>
                </v-col>

                <v-col cols="12" md="6">
                  <ValidationProvider name="Público alvo" rules="required" v-slot="{ errors }">
                    <v-select
                      id="atendimento.publicoAlvo"
                      v-model="form.publico_alvo"
                      :items="publicoOpcoes"
                      item-text="text"
                      item-value="value"
                      label="Público alvo"
                      class="titulo-maior"
                      multiple
                      outlined
                      dense
                      :disabled="apenasParaAlunos"
                      :hint="
                        apenasParaAlunos ? 'Este tipo de atendimento é apenas para alunos.' : ''
                      "
                      persistent-hint
                      :error-messages="errors"
                    />
                  </ValidationProvider>
                </v-col>

                <v-col cols="12" md="6">
                  <ValidationProvider
                    name="Professor Orientador"
                    :rules="{ required: true, no_special_chars: true, max_chars: 100 }"
                    v-slot="{ errors }"
                  >
                    <v-text-field
                      id="atendimento.professorOrientador"
                      ref="professorField"
                      v-model="form.professor_orientador"
                      @input="onProfessorInput"
                      label="Professor Orientador"
                      outlined
                      dense
                      counter="100"
                      maxlength="100"
                      :error-messages="professorErro || errors"
                    />
                  </ValidationProvider>
                </v-col>

                <v-col cols="12" md="4">
                  <ValidationProvider
                    name="Carga Horária"
                    :rules="{ required: true, max_chars: 10 }"
                    v-slot="{ errors }"
                  >
                    <v-text-field
                      id="atendimento.cargaHoraria"
                      ref="cargaField"
                      v-model="form.carga_horaria"
                      @input="onCargaInput"
                      label="Carga Horária"
                      outlined
                      dense
                      counter="10"
                      maxlength="10"
                      :error-messages="cargaErro || errors"
                    />
                  </ValidationProvider>
                </v-col>

                <v-col v-if="mostrarCurso" cols="12" md="8">
                  <ValidationProvider
                    name="Curso"
                    :rules="{ required: true, no_special_chars: true, max_chars: 100 }"
                    v-slot="{ errors }"
                  >
                    <v-text-field
                      id="atendimento.curso"
                      v-model="form.curso"
                      label="Curso"
                      outlined
                      dense
                      counter="100"
                      maxlength="100"
                      :error-messages="errors"
                    />
                  </ValidationProvider>
                </v-col>

                <v-col v-if="mostrarDisciplinas" cols="12" md="8">
                  <ValidationProvider
                    name="Disciplina(s)"
                    :rules="{ required: true, no_special_chars: true, max_chars: 100 }"
                    v-slot="{ errors }"
                  >
                    <v-text-field
                      id="atendimento.disciplinas"
                      v-model="form.disciplinas"
                      label="Disciplina(s)"
                      outlined
                      dense
                      counter="100"
                      maxlength="100"
                      :error-messages="errors"
                    />
                  </ValidationProvider>
                </v-col>

                <v-col v-if="mostrarHabilidades" cols="12">
                  <ValidationProvider
                    name="Habilidades"
                    :rules="{ required: true, no_special_chars: true, max_chars: 500 }"
                    v-slot="{ errors }"
                  >
                    <v-textarea
                      id="atendimento.habilidades"
                      v-model="form.habilidades"
                      label="Habilidades"
                      outlined
                      dense
                      rows="3"
                      counter="500"
                      maxlength="500"
                      :error-messages="errors"
                    />
                  </ValidationProvider>
                </v-col>

                <v-col cols="12">
                  <ValidationProvider
                    name="Conteúdo Ministrado"
                    :rules="{ required: true, no_special_chars: true, max_chars: 1000 }"
                    v-slot="{ errors }"
                  >
                    <v-textarea
                      id="atendimento.conteudo"
                      ref="conteudoField"
                      v-model="form.conteudo_ministrado"
                      @input="onConteudoInput"
                      label="Conteúdo Ministrado"
                      outlined
                      dense
                      rows="4"
                      counter="1000"
                      maxlength="1000"
                      :error-messages="conteudoErro || errors"
                    />
                  </ValidationProvider>
                </v-col>
              </v-row>
            </ValidationObserver>
          </v-card-text>
        </v-tab-item>

        <!-- ABA 2: FREQUÊNCIA -->
        <v-tab-item>
          <v-card-text>
            <!-- Alunos -->
            <div v-if="publicoIncluiAlunos">
              <div class="d-flex align-center mb-2">
                <v-icon left color="primary">mdi-account-search</v-icon>
                <span class="text-h6">Alunos</span>
              </div>

              <e-autocomplete
                id="atendimento.buscaAluno"
                :items="resultadosFiltrados"
                :return-object="true"
                :item-value="(m) => m.matricula_id"
                :item-text="itemTextAluno"
                :loading="carregandoAlunos"
                :value="alunoSelecionado"
                label="Pesquisar e adicionar aluno..."
                dense
                clearable
                @change="onSelecionarAluno"
              />

              <v-simple-table dense class="mb-4">
                <template v-slot:default>
                  <thead>
                    <tr>
                      <th>Aluno</th>
                      <th class="text-center">PcD</th>
                      <th class="text-center">Presença</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-if="!participantesAlunos.length">
                      <td colspan="4" class="text-center grey--text">Nenhum aluno adicionado</td>
                    </tr>
                    <tr v-for="(a, i) in participantesAlunos" :key="a.matricula_id">
                      <td>{{ a.nome }}</td>
                      <td class="text-center">
                        <v-chip v-if="a.especial" x-small color="blue" dark>PcD</v-chip>
                        <span v-else>- - -</span>
                      </td>
                      <td class="text-center">
                        <v-btn-toggle :value="a.presente ? 0 : 1">
                          <v-btn
                            :id="`atendimento.presente-${a.matricula_id}`"
                            :color="a.presente ? 'green' : 'grey lighten-2'"
                            small
                            @click="a.presente = true"
                          >
                            <v-icon :class="a.presente ? 'white--text' : ''" x-small>
                              fa fa-thumbs-up
                            </v-icon>
                          </v-btn>
                          <v-btn
                            :id="`atendimento.falta-${a.matricula_id}`"
                            :color="!a.presente ? 'red' : 'grey lighten-2'"
                            small
                            @click="a.presente = false"
                          >
                            <v-icon :class="!a.presente ? 'white--text' : ''" x-small>
                              fa fa-thumbs-down
                            </v-icon>
                          </v-btn>
                        </v-btn-toggle>
                      </td>
                      <td class="text-right">
                        <v-btn icon color="error" x-small @click="removerAluno(i)">
                          <v-icon small>fa fa-trash</v-icon>
                        </v-btn>
                      </td>
                    </tr>
                  </tbody>
                </template>
              </v-simple-table>
            </div>

            <!-- Pais / Funcionários -->
            <div v-if="publicoIncluiExternos">
              <v-divider v-if="publicoIncluiAlunos" class="mb-4" />
              <div class="d-flex align-center justify-space-between mb-2">
                <div class="d-flex align-center">
                  <v-icon left color="primary">mdi-account-multiple</v-icon>
                  <span class="text-h6">Pais / Funcionários</span>
                </div>
                <v-btn small color="primary" dark @click="adicionarExterno">
                  <v-icon left small>mdi-plus</v-icon>
                  Adicionar participante
                </v-btn>
              </div>

              <v-simple-table dense class="mb-4">
                <template v-slot:default>
                  <thead>
                    <tr>
                      <th style="width: 160px">Tipo</th>
                      <th>Nome completo</th>
                      <th style="width: 200px">CPF</th>
                      <th class="text-center">Presença</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-if="!participantesExternos.length">
                      <td colspan="5" class="text-center grey--text">
                        Nenhum participante adicionado
                      </td>
                    </tr>
                    <tr v-for="(e, i) in participantesExternos" :key="i">
                      <td>
                        <v-select
                          v-model="e.tipo"
                          :items="tipoExternoOpcoes"
                          item-text="text"
                          item-value="value"
                          dense
                          hide-details
                        />
                      </td>
                      <td>
                        <v-text-field
                          v-model="e.nome_completo"
                          dense
                          hide-details
                          maxlength="150"
                        />
                      </td>
                      <td>
                        <v-text-field
                          v-model="e.cpf"
                          v-mask="'###.###.###-##'"
                          dense
                          hide-details
                          placeholder="Opcional"
                        />
                      </td>
                      <td class="text-center">
                        <v-btn-toggle :value="e.presente ? 0 : 1">
                          <v-btn
                            :color="e.presente ? 'green' : 'grey lighten-2'"
                            small
                            @click="e.presente = true"
                          >
                            <v-icon :class="e.presente ? 'white--text' : ''" x-small>
                              fa fa-thumbs-up
                            </v-icon>
                          </v-btn>
                          <v-btn
                            :color="!e.presente ? 'red' : 'grey lighten-2'"
                            small
                            @click="e.presente = false"
                          >
                            <v-icon :class="!e.presente ? 'white--text' : ''" x-small>
                              fa fa-thumbs-down
                            </v-icon>
                          </v-btn>
                        </v-btn-toggle>
                      </td>
                      <td class="text-right">
                        <v-btn icon color="error" x-small @click="removerExterno(i)">
                          <v-icon small>fa fa-trash</v-icon>
                        </v-btn>
                      </td>
                    </tr>
                  </tbody>
                </template>
              </v-simple-table>
            </div>

            <div
              v-if="!publicoIncluiAlunos && !publicoIncluiExternos"
              class="grey--text py-6 text-center"
            >
              Selecione o público alvo na aba "Dados Principais" para registrar a frequência.
            </div>

            <div
              v-if="publicoIncluiAlunos || publicoIncluiExternos"
              class="d-flex justify-space-between align-center flex-wrap mt-2"
            >
              <div class="d-flex align-center flex-wrap">
                <span class="text-body-2 font-weight-medium mr-4">
                  <v-icon small class="mr-1 text--secondary">mdi-information-outline</v-icon>
                  Verifique as presenças antes de confirmar
                </span>
                <span class="text-caption text--secondary mr-2 font-weight-medium">Legenda:</span>
                <v-chip x-small color="green" text-color="white" class="mr-2 pa-3">
                  <v-icon x-small left>fa fa-thumbs-up</v-icon>
                  Presença
                </v-chip>
                <v-chip x-small color="red" text-color="white" class="pa-3">
                  <v-icon x-small left>fa fa-thumbs-down</v-icon>
                  Falta
                </v-chip>
              </div>

              <v-btn
                id="atendimento.confirmarFrequenciaBtn"
                color="green darken-1"
                dark
                :loading="salvando"
                @click="showConfirmacaoFrequencia = true"
              >
                <v-icon left>mdi-check-circle</v-icon>
                Confirmar Frequência
              </v-btn>
            </div>
          </v-card-text>
        </v-tab-item>
      </v-tabs-items>

      <template v-if="tab === 0">
        <v-divider />
        <v-card-actions class="pa-4">
          <v-spacer />
          <v-btn text @click="voltar">Cancelar</v-btn>
          <v-btn
            id="atendimento.salvarBtn"
            color="primary"
            dark
            :loading="salvando"
            @click="salvar"
          >
            Salvar
          </v-btn>
        </v-card-actions>
      </template>
    </v-card>

    <EModalConfirmacao
      v-model="showConfirmacaoFrequencia"
      titulo="Confirmar Frequência"
      acao="confirmar a frequência deste atendimento"
      icon="mdi-account-check"
      icon-color="primary"
      texto-confirmar="Sim, Confirmar"
      cor-botao-confirmar="success"
      icone-botao-confirmar="mdi-check-circle"
      @confirmado="confirmarFrequencia"
    />
  </main-template>
</template>

<script>
import EModalConfirmacao from "@/components/EModalConfirmacao.vue";

export default {
  name: "NewEditAtendimentoPage",

  components: { EModalConfirmacao },

  props: {
    editing: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      tab: 0,
      salvando: false,
      menuData: false,
      showConfirmacaoFrequencia: false,
      presencasConfirmadas: false,
      professorErro: "",
      cargaErro: "",
      conteudoErro: "",
      tipos: [],
      franquias: [],
      carregandoAlunos: false,
      alunoSelecionado: null,
      resultadosBusca: [],
      participantesAlunos: [],
      participantesExternos: [],
      publicoOpcoes: [
        { value: "alunos", text: "Alunos" },
        { value: "pais", text: "Pais" },
        { value: "funcionarios", text: "Funcionários" },
      ],
      tipoExternoOpcoes: [
        { value: "pai", text: "Pai/Responsável" },
        { value: "funcionario", text: "Funcionário" },
      ],
      form: {
        id: null,
        configuracao_id: null,
        tipo_atendimento_id: null,
        data: "",
        horario: "",
        publico_alvo: [],
        zona: "urbana",
        professor_orientador: "",
        carga_horaria: "",
        curso: "",
        disciplinas: "",
        habilidades: "",
        conteudo_ministrado: "",
      },
    };
  },

  computed: {
    tipoSelecionado() {
      return this.tipos.find((t) => t.id === this.form.tipo_atendimento_id) || null;
    },
    categoria() {
      return this.tipoSelecionado ? this.tipoSelecionado.categoria : null;
    },
    apenasParaAlunos() {
      return this.tipoSelecionado ? Boolean(this.tipoSelecionado.apenas_para_alunos) : false;
    },
    mostrarCurso() {
      return this.categoria === "curso";
    },
    mostrarHabilidades() {
      return this.categoria === "caee";
    },
    mostrarDisciplinas() {
      return this.categoria === "recomposicao";
    },
    publicoIncluiAlunos() {
      return (this.form.publico_alvo || []).includes("alunos");
    },
    publicoIncluiExternos() {
      const p = this.form.publico_alvo || [];
      return p.includes("pais") || p.includes("funcionarios");
    },
    resultadosFiltrados() {
      return this.resultadosBusca.filter((m) => !this.alunoJaAdicionado(m.matricula_id));
    },
  },

  watch: {
    tab(novo) {
      // ao abrir a aba de Frequência com público de alunos, já busca todos
      if (novo === 1 && this.publicoIncluiAlunos && !this.resultadosBusca.length) {
        this.pesquisarAlunos();
      }
    },
  },

  async mounted() {
    this.$loaderService.open("Carregando dados...");
    try {
      await Promise.all([this.carregarTipos(), this.carregarFranquias()]);
      if (this.editing) {
        await this.carregarAtendimento();
      } else {
        this.form.configuracao_id = this.$store.getters["Auth/user"]?.franquia?.id || null;
      }
      if (this.$route.query.tab === "frequencia") {
        this.tab = 1;
      }
    } catch (error) {
      this.$handleError(error);
    }
    this.$loaderService.close();
  },

  methods: {
    aplicarValorSaneado(ref, campo, limpo) {
      this.form[campo] = limpo;
      const field = this.$refs[ref];
      if (field) field.lazyValue = limpo;
    },

    onProfessorInput(valor) {
      const limpo = this.$semEmojisNemEspeciais(valor);
      this.professorErro =
        limpo !== (valor || "")
          ? "Não são permitidos emojis ou caracteres especiais inválidos"
          : "";
      this.aplicarValorSaneado("professorField", "professor_orientador", limpo);
    },

    onConteudoInput(valor) {
      const limpo = this.$semEmojisNemEspeciais(valor);
      this.conteudoErro =
        limpo !== (valor || "")
          ? "Não são permitidos emojis ou caracteres especiais inválidos"
          : "";
      this.aplicarValorSaneado("conteudoField", "conteudo_ministrado", limpo);
    },

    onCargaInput(valor) {
      const limpo = this.$somenteNumeros(valor);
      this.cargaErro = limpo !== (valor || "") ? "Neste campo são permitidos apenas números" : "";
      this.aplicarValorSaneado("cargaField", "carga_horaria", limpo);
    },

    async carregarTipos() {
      this.tipos = await this.$services.tiposDeAtendimentoService.syncAll();
    },

    async carregarFranquias() {
      try {
        this.franquias = await this.$services.franquiasService.listAllSelect();
      } catch (error) {
        const franquia = this.$store.getters["Auth/user"]?.franquia;
        this.franquias = franquia ? [franquia] : [];
      }
    },

    async carregarAtendimento() {
      const id = parseInt(this.$route.params.atendimento_id, 10);
      if (!Number.isInteger(id)) return;
      const atendimento = await this.$services.atendimentosService.find(id);
      this.form = {
        id: atendimento.id,
        configuracao_id: atendimento.configuracao_id,
        tipo_atendimento_id: atendimento.tipo_atendimento_id,
        data: atendimento.data,
        horario: atendimento.horario,
        publico_alvo: atendimento.publico_alvo || [],
        zona: atendimento.zona,
        professor_orientador: atendimento.professor_orientador,
        carga_horaria: atendimento.carga_horaria,
        curso: atendimento.curso,
        disciplinas: atendimento.disciplinas,
        habilidades: atendimento.habilidades,
        conteudo_ministrado: atendimento.conteudo_ministrado,
      };
      this.presencasConfirmadas = Boolean(atendimento.presencas_confirmadas);

      (atendimento.participantes || []).forEach((p) => {
        if (p.tipo === "aluno") {
          this.participantesAlunos.push({
            matricula_id: p.matricula_id,
            nome: p.matricula && p.matricula.aluno ? p.matricula.aluno.nomecompleto : "Aluno",
            codigo: p.matricula && p.matricula.aluno ? p.matricula.aluno.codigo : null,
            especial:
              p.matricula && p.matricula.aluno ? Boolean(p.matricula.aluno.especial) : false,
            presente: Boolean(p.presente),
          });
        } else {
          this.participantesExternos.push({
            tipo: p.tipo,
            nome_completo: p.nome_completo,
            cpf: p.cpf,
            presente: Boolean(p.presente),
          });
        }
      });
    },

    onChangeTipo() {
      if (this.apenasParaAlunos) {
        this.form.publico_alvo = ["alunos"];
      }
      // limpa campos extras que não pertencem à categoria selecionada
      if (!this.mostrarCurso) this.form.curso = "";
      if (!this.mostrarDisciplinas) this.form.disciplinas = "";
      if (!this.mostrarHabilidades) this.form.habilidades = "";
    },

    async pesquisarAlunos() {
      this.carregandoAlunos = true;
      try {
        this.resultadosBusca = await this.$services.atendimentosService.buscarMatriculas(
          null,
          this.form.configuracao_id
        );
      } catch (error) {
        this.$handleError(error);
      }
      this.carregandoAlunos = false;
    },

    itemTextAluno(m) {
      return `${m.nome}${m.especial ? " (PcD)" : ""} — ${m.codigo || "sem código"}`;
    },

    onSelecionarAluno(m) {
      if (m) this.adicionarAluno(m);
      // limpa a seleção para permitir adicionar o próximo aluno
      this.$nextTick(() => {
        this.alunoSelecionado = null;
      });
    },

    alunoJaAdicionado(matriculaId) {
      return this.participantesAlunos.some((a) => a.matricula_id === matriculaId);
    },

    adicionarAluno(m) {
      if (this.alunoJaAdicionado(m.matricula_id)) return;
      this.participantesAlunos.push({
        matricula_id: m.matricula_id,
        nome: m.nome,
        codigo: m.codigo,
        especial: m.especial,
        presente: true,
      });
    },

    removerAluno(index) {
      this.participantesAlunos.splice(index, 1);
    },

    adicionarExterno() {
      this.participantesExternos.push({
        tipo: this.form.publico_alvo.includes("pais") ? "pai" : "funcionario",
        nome_completo: "",
        cpf: "",
        presente: true,
      });
    },

    removerExterno(index) {
      this.participantesExternos.splice(index, 1);
    },

    montarParticipantes() {
      const participantes = [];
      if (this.publicoIncluiAlunos) {
        this.participantesAlunos.forEach((a) => {
          participantes.push({ tipo: "aluno", matricula_id: a.matricula_id, presente: a.presente });
        });
      }
      if (this.publicoIncluiExternos) {
        this.participantesExternos.forEach((e) => {
          participantes.push({
            tipo: e.tipo,
            nome_completo: e.nome_completo,
            cpf: e.cpf || null,
            presente: e.presente,
          });
        });
      }
      return participantes;
    },

    montarPayload(confirmarFrequencia) {
      const payload = {
        configuracao_id: this.form.configuracao_id,
        tipo_atendimento_id: this.form.tipo_atendimento_id,
        data: this.form.data,
        horario: this.form.horario,
        publico_alvo: this.form.publico_alvo,
        zona: this.form.zona,
        professor_orientador: this.form.professor_orientador,
        carga_horaria: this.form.carga_horaria,
        curso: this.mostrarCurso ? this.form.curso : null,
        disciplinas: this.mostrarDisciplinas ? this.form.disciplinas : null,
        habilidades: this.mostrarHabilidades ? this.form.habilidades : null,
        conteudo_ministrado: this.form.conteudo_ministrado,
        participantes: this.montarParticipantes(),
      };

      if (confirmarFrequencia) {
        payload.confirmar_frequencia = true;
      }

      return payload;
    },

    async persistir(confirmarFrequencia) {
      const valido = await this.$refs.observer.validate();
      if (!valido) {
        this.tab = 0;
        this.$toast.danger("Preencha os campos obrigatórios em Dados Principais.");
        return false;
      }

      if (
        this.publicoIncluiExternos &&
        this.participantesExternos.some((e) => !e.nome_completo || !e.nome_completo.trim())
      ) {
        this.tab = 1;
        this.$toast.danger(
          "Informe o nome completo de todos os participantes (pais/funcionários) adicionados."
        );
        return false;
      }

      this.salvando = true;
      try {
        const payload = this.montarPayload(confirmarFrequencia);
        if (this.form.id) {
          await this.$services.atendimentosService.atualizar(this.form.id, payload);
        } else {
          const criado = await this.$services.atendimentosService.criar(payload);
          this.form.id = criado.id;
        }
        return true;
      } catch (error) {
        this.$handleError(error);
        return false;
      } finally {
        this.salvando = false;
      }
    },

    async salvar() {
      const ok = await this.persistir(false);
      if (ok) {
        this.$toast.success("Atendimento salvo com sucesso");
        this.tab = 1;
      }
    },

    async confirmarFrequencia() {
      const ok = await this.persistir(true);
      if (ok) {
        this.presencasConfirmadas = true;
        this.$toast.success("Frequência confirmada com sucesso");
        this.voltar();
      }
    },

    formatarData(data) {
      if (!data) return "";
      const [ano, mes, dia] = data.split("-");
      return `${dia}/${mes}/${ano}`;
    },

    voltar() {
      this.$router.push({ name: "diarioAtendimentos.atendimentos" });
    },
  },
};
</script>

<style scoped>
.horario-pointer ::v-deep input {
  cursor: pointer;
}

.titulo-maior ::v-deep .v-label {
  font-size: 1.2rem;
}
</style>
